import { 
    Autocomplete, 
    Button, 
    TextField, 
    Typography, 
    Box, 
    Paper, 
    Stack, 
    InputAdornment,
    Chip,
    CircularProgress,
    Grid
} from '@mui/material';
import './circuitPage.scss';
import { 
    Refresh, 
    Add, 
    Search as SearchIcon, 
    Explore,
    Hotel,
    LocalTaxi,
    Hiking,
    FolderShared
} from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import circuitSlice, { fetchCircuits } from '../../data/slices/circuitSlice';
import StyledTable from '../../components/table-component/StyledTable';
import { fetchToClient } from '../../data/slices/toClientSlice';
import PageHeader from '../../components/layout/PageHeader';

const CircuitPage = (props) => {
    const { type } = useParams();
    const navigateTo = useNavigate();
    const dispatch = useDispatch();
    const { circuits, fetchStatus } = useSelector((state) => state.circuits);
    const { toClients } = useSelector((state) => state.toClients);
    const { filterCircuits } = circuitSlice.actions;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientInput, setClientInput] = useState('');

    useEffect(() => {
        dispatch(fetchCircuits(type));
        dispatch(fetchToClient());
    }, [type, dispatch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(filterCircuits({
                nail: searchTerm,
                clientId: selectedClient ? selectedClient.id : null
            }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, selectedClient, dispatch, filterCircuits]);

    const getPageConfig = () => {
        switch (type) {
            case 'circuit': return { title: 'Circuits Touristiques', icon: Explore, color: '#0284c7' };
            case 'sejour': return { title: 'Séjours & Hôtels', icon: Hotel, color: '#7c3aed' };
            case 'excursion': return { title: 'Excursions', icon: Hiking, color: '#059669' };
            case 'transfert': return { title: 'Transferts', icon: LocalTaxi, color: '#ea580c' };
            default: return { title: `Dossiers ${type}s`, icon: FolderShared, color: COLORS.primary };
        }
    };

    const config = getPageConfig();

    const processedRows = useMemo(() => {
        return [...circuits].sort((a, b) => {
            const dateA = (a.dateVolArrivee || a.date) ? new Date(a.dateVolArrivee || a.date).getTime() : 0;
            const dateB = (b.dateVolArrivee || b.date) ? new Date(b.dateVolArrivee || b.date).getTime() : 0;
            if (dateB !== dateA) return dateB - dateA;
            return (b.id || 0) - (a.id || 0);
        }).map(circuit => {
            let statusLabel, statusColor;
            if (circuit.clotureDefinitive) {
                statusLabel = 'Définitif'; statusColor = COLORS.secondary;
            } else if (circuit.preCloture) {
                statusLabel = 'Pré-clôturé'; statusColor = COLORS.warning;
            } else if (circuit.cloture) {
                statusLabel = 'Clôturé'; statusColor = COLORS.info;
            } else {
                statusLabel = 'Ouvert'; statusColor = COLORS.success;
            }
            
            return {
                ...circuit,
                client: (() => {
                    const c = circuit.toClient || circuit.client;
                    if (c) return typeof c === 'string' ? c : (c.libelle || c.nomComplet || c.nom || '-');
                    // Client individuel : nom saisi en texte libre
                    if (circuit.nomClient) return circuit.nomClient;
                    return '-';
                })(),
                dateDebut: circuit.dateVolArrivee || circuit.date || '-',
                numeroFacture: circuit.numeroFacture || (circuit.factureCircuits?.[circuit.factureCircuits.length - 1]?.numero || '-'),
                displayStatut: <Chip label={statusLabel} size="small" sx={{ bgcolor: statusColor + '20', color: statusColor, fontWeight: 700, borderRadius: '6px' }} />
            };
        });
    }, [circuits]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title={config.title}
                subtitle={`Gérez vos dossiers de type ${type} et suivez leur état d'avancement`}
                icon={config.icon}
                breadcrumbs={[
                    { label: 'Dossiers', path: '/dossiers' },
                    { label: config.title, path: `/dossiers/${type}` }
                ]}
                actions={[
                    { 
                        label: type === 'excursion' ? `Nouvelle ${type}` : `Nouveau ${type}`, 
                        icon: <Add />, 
                        onClick: () => navigateTo('/ajout/dossier/' + type),
                        color: 'primary'
                    }
                ]}
            />

            {/* Filters Bar */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Référence, N° facture..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: COLORS.gray[400] }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={toClients || []}
                            getOptionLabel={(option) => option.libelle || ''}
                            value={selectedClient}
                            inputValue={clientInput}
                            onInputChange={(e, value) => setClientInput(value)}
                            onChange={(e, value) => setSelectedClient(value)}
                            size="small"
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Filtrer par partenaire..." />
                            )}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button 
                                variant="text" 
                                startIcon={<Refresh />} 
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedClient(null);
                                    setClientInput('');
                                    dispatch(fetchCircuits(type));
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Réinitialiser
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ 
                borderRadius: '16px', 
                overflow: 'hidden', 
                border: '1px solid',
                borderColor: COLORS.gray[200],
                bgcolor: 'white'
            }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: COLORS.gray[100], bgcolor: COLORS.gray[50] }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Liste des dossiers ({processedRows.length})
                    </Typography>
                </Box>
                
                {fetchStatus === 'loading' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <StyledTable
                        rows={processedRows}
                        head={['reference', 'client', 'methodePaiement', 'numeroFacture', 'displayStatut', 'dateDebut']}
                        headLabels={{ 
                            client: 'Partenaire/Client', 
                            displayStatut: 'Statut', 
                            numeroFacture: 'Facture',
                            methodePaiement: 'Paiement',
                            dateDebut: 'Date'
                        }}
                        onOptionClicked={(id) => navigateTo(`/dossiers/${type}/${id}`)}
                    />
                )}
            </Paper>
        </Box>
    );
}

export default CircuitPage;
